import { Inject, Injectable } from '@angular/core';
import * as store from '@daas-consulting/core-frontend-store';
import {
  AgeGroupsService,
  AgeGroupsServiceId,
} from '@daas-consulting/production/age_groups/frontend/data_contracts';
import * as models from '@daas-consulting/production/age_groups/frontend/models';
import {
  errorMessage,
  loader,
} from '@daas-consulting/production/shared/frontend/store';
import { Action, State, StateContext, ofActionDispatched } from '@ngxs/store';
import { patch } from '@ngxs/store/operators';
import { catchError, take, tap } from 'rxjs';
import * as actions from './age_groups-list.actions';
import * as consts from './age_groups-list.const';
import { AgeGroupsListStateModel } from './age_groups-list.state-model';

export const defaults: AgeGroupsListStateModel = {
  items: [],
};

@State<AgeGroupsListStateModel>({
  name: consts.stateTokenName,
  defaults,
})
@Injectable()
export class AgeGroupsListState {
  readonly #service: AgeGroupsService;
  readonly #eventHelper: store.EventHelper;
  constructor(
    @Inject(AgeGroupsServiceId) service: AgeGroupsService,
    @Inject(store.EventHelper) eventHelper: store.EventHelper
  ) {
    this.#service = service;
    this.#eventHelper = eventHelper;
    this._connectEvents();
  }

  @Action(actions.LoadItemsRequest)
  loadItemsRequest(ctx: StateContext<AgeGroupsListStateModel>): void {
    this.#service
      .getAll$()
      .pipe(
        take(1),
        tap((items: models.list.ItemView[]) => {
          ctx.dispatch(new actions.LoadItemsSuccessful(items));
        }),
        catchError((error) => {
          ctx.dispatch(new actions.LoadItemsFailure(error as Error));
          return store.handleError(error);
        })
      )
      .subscribe();
  }

  @Action(actions.LoadItemsSuccessful)
  loadItemsSuccessful(
    ctx: StateContext<AgeGroupsListStateModel>,
    { payload }: actions.LoadItemsSuccessful
  ): void {
    const items = (payload || [])
      .map(this._mapAgeGroup)
      .sort((a, b) => a.maxAge - b.maxAge);
    ctx.setState(
      patch({
        items: items,
      })
    );
  }

  @Action(actions.LoadItemsFailure)
  loadItemsFailure(
    ctx: StateContext<AgeGroupsListStateModel>,
    { payload }: actions.LoadItemsFailure
  ) {
    const action = new errorMessage.actions.ShowError(payload);
    return ctx.dispatch(action);
  }

  private _mapAgeGroup(value: models.list.AgeGroup): models.list.AgeGroup {
    let color;
    let name;
    switch (value.type) {
      case models.list.AgeGroupType.neo:
        color = models.list.Colors.amber;
        name = 'Neo';
        break;

      case models.list.AgeGroupType.pediatric:
        color = models.list.Colors.cyan;
        name = 'Pediátrico';
        break;

      case models.list.AgeGroupType.adults:
        color = models.list.Colors.purple;
        name = 'Adulto';
        break;

      default:
        color = models.list.Colors.red;
        name = 'S/N';
    }

    return {
      ...value,
      color,
      name,
    };
  }

  private _connectEvents(): void {
    this.#eventHelper.connectEvents(
      actions.LoadItems,
      loader.actions.ShowLoading,
      ofActionDispatched
    );

    this.#eventHelper.connectEvents(
      actions.LoadItems,
      actions.LoadItemsRequest
    );

    this.#eventHelper.connectEvents(
      actions.LoadItemsSuccessful,
      loader.actions.HideLoading,
      ofActionDispatched
    );

    this.#eventHelper.connectEvents(
      actions.LoadItemsFailure,
      loader.actions.HideLoading,
      ofActionDispatched
    );
  }
}
